import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useLang, IMAGES } from "../i18n";
import { Search, X, ArrowUpRight, ChevronDown } from "lucide-react";

const normalize = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
export const Nav = () => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const home = pathname === "/";
  const anchor = (id) => (home ? `#${id}` : `/#${id}`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  const links = [
    { id: "demo", href: anchor("demo"), label: t.nav.demo },
    { id: "patents", dropdown: true, label: t.nav.patents },
    { id: "platform", href: anchor("platform"), label: t.nav.platform },
    { id: "services", href: anchor("services"), label: t.nav.services },
    { id: "sectors", href: anchor("sectors"), label: t.nav.sectors },
    { id: "tenute", to: "/tenute", label: t.nav.tenute },
  ];

  const mobileLinks = [
    { id: "demo", href: anchor("demo"), label: t.nav.demo },
    { id: "about", href: anchor("about"), label: t.nav.about },
    { id: "patent1", to: "/brevetti/etichetta-intelligente", label: `01 · ${t.nav.patent1}` },
    { id: "patent2", to: "/brevetti/oregano-scan", label: `02 · ${t.nav.patent2}` },
    { id: "platform", href: anchor("platform"), label: t.nav.platform },
    { id: "services", href: anchor("services"), label: t.nav.services },
    { id: "vision", href: anchor("vision"), label: t.nav.vision },
    { id: "sectors", href: anchor("sectors"), label: t.nav.sectors },
    { id: "tenute", to: "/tenute", label: t.nav.tenute },
    { id: "contact", href: anchor("contact"), label: t.nav.contact },
  ];

  const q = normalize(query.trim());
  const results = q
    ? t.search.items.filter((it) => {
        const hay = normalize(`${it.label} ${it.desc} ${it.keywords}`);
        return q.split(/\s+/).every((w) => hay.includes(w));
      })
    : [];

  const goTo = (target) => {
    setSearchOpen(false);
    setQuery("");
    setOpen(false);
    if (target.startsWith("#")) {
      if (home) {
        document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate("/");
        setTimeout(() => {
          document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
        }, 500);
      }
    } else {
      navigate(target);
    }
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled || searchOpen ? "glass border-b border-[var(--border)]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a href={home ? "#top" : "/"} data-testid="logo" className="flex items-center group">
          <img
            src={IMAGES.logo}
            alt="Amazonico3000"
            className="h-11 md:h-14 w-auto transition-transform duration-500 group-hover:scale-105"
          />
        </a>

        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) =>
            l.dropdown ? (
              <div key={l.id} className="relative group">
                <button
                  data-testid="nav-patents"
                  className="relative font-outfit text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-300 flex items-center gap-1.5 after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[var(--gold)] hover:after:w-full after:transition-all after:duration-300"
                >
                  {l.label}
                  <ChevronDown className="w-3.5 h-3.5 transition-transform duration-300 group-hover:rotate-180" />
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                  <div className="glass rounded-2xl border border-[var(--border)] p-2 w-80 shadow-[0_30px_60px_-20px_rgba(30,43,35,0.35)]">
                    {t.chooser.cards.map((c, i) => (
                      <Link
                        key={c.link}
                        to={c.link}
                        data-testid={`nav-patent-${i + 1}`}
                        className="block rounded-xl px-4 py-3 hover:bg-[var(--bg-secondary)] transition-colors"
                      >
                        <span className="font-jetbrains text-[10px] tracking-[0.2em] text-[var(--gold)] uppercase">{c.n}</span>
                        <span className="block font-cormorant text-xl text-[var(--text-primary)] leading-tight mt-0.5">{c.name}</span>
                        <span className="block font-outfit font-light text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">{c.d}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : l.to ? (
              <Link
                key={l.id}
                to={l.to}
                data-testid={`nav-${l.id}`}
                className="relative font-outfit text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[var(--gold)] hover:after:w-full after:transition-all after:duration-300"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-${l.id}`}
                className="relative font-outfit text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[var(--gold)] hover:after:w-full after:transition-all after:duration-300"
              >
                {l.label}
              </a>
            )
          )}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setSearchOpen((o) => !o);
              setOpen(false);
            }}
            data-testid="nav-search-btn"
            aria-label="Cerca"
            className="w-9 h-9 rounded-full border border-[var(--border)] flex items-center justify-center text-[var(--text-secondary)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors duration-300"
          >
            {searchOpen ? <X className="w-4 h-4" /> : <Search className="w-4 h-4" />}
          </button>
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="font-jetbrains text-xs tracking-widest border border-[var(--border)] rounded-full px-3 py-1.5 hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors duration-300"
          >
            <span className={lang === "it" ? "text-[var(--gold)]" : "text-[var(--text-secondary)]"}>IT</span>
            <span className="text-[var(--text-secondary)] mx-1">/</span>
            <span className={lang === "en" ? "text-[var(--gold)]" : "text-[var(--text-secondary)]"}>EN</span>
          </button>
          <button
            onClick={() => {
              setOpen((o) => !o);
              setSearchOpen(false);
            }}
            data-testid="mobile-menu-btn"
            className="lg:hidden font-jetbrains text-xs tracking-widest text-[var(--text-primary)]"
          >
            {open ? "CLOSE" : "MENU"}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            data-testid="nav-search-panel"
            className="overflow-hidden glass border-t border-[var(--border)]"
          >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5">
              <div className="flex items-center gap-3 border-b border-[var(--border)] pb-3">
                <Search className="w-4 h-4 text-[var(--gold)] shrink-0" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="nav-search-input"
                  placeholder={t.search.placeholder}
                  className="w-full bg-transparent font-outfit font-light text-lg text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]/60 focus:outline-none"
                />
                {query && (
                  <button
                    onClick={() => setQuery("")}
                    data-testid="nav-search-clear"
                    aria-label="Pulisci"
                    className="text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {!q && (
                <p data-testid="nav-search-hint" className="font-outfit font-light text-sm text-[var(--text-secondary)] pt-4">
                  {t.search.hint}
                </p>
              )}
              {q && results.length === 0 && (
                <p data-testid="nav-search-empty" className="font-outfit font-light text-sm text-[var(--text-secondary)] pt-4">
                  {t.search.noResults}
                </p>
              )}
              {results.length > 0 && (
                <ul className="divide-y divide-[var(--border)] max-h-[55vh] overflow-y-auto">
                  {results.map((r, i) => (
                    <li key={r.target}>
                      <button
                        onClick={() => goTo(r.target)}
                        data-testid={`nav-search-result-${i}`}
                        className="w-full flex items-center justify-between gap-4 py-3.5 text-left group"
                      >
                        <span>
                          <span className="font-cormorant text-xl md:text-2xl text-[var(--text-primary)] group-hover:text-[var(--gold)] transition-colors">
                            {r.label}
                          </span>
                          <span className="block font-outfit font-light text-sm text-[var(--text-secondary)]">
                            {r.desc}
                          </span>
                        </span>
                        <ArrowUpRight className="w-4 h-4 shrink-0 text-[var(--text-secondary)] group-hover:text-[var(--gold)] group-hover:rotate-45 transition-all duration-300" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden glass border-t border-[var(--border)]"
          >
            <div className="px-6 py-6 flex flex-col">
              {mobileLinks.map((l) =>
                l.to ? (
                  <Link
                    key={l.id}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.id}`}
                    className="font-cormorant text-2xl text-[var(--text-primary)] py-3 border-b border-[var(--border)]/60 last:border-b-0 hover:text-[var(--gold)] transition-colors"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.id}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.id}`}
                    className="font-cormorant text-2xl text-[var(--text-primary)] py-3 border-b border-[var(--border)]/60 last:border-b-0 hover:text-[var(--gold)] transition-colors"
                  >
                    {l.label}
                  </a>
                )
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
